/**
 * Shared audit-log vocabulary and formatting for POS operator activity.
 *
 * Constraint: we work within the existing `/api/admin/insertlog` API, which
 * persists into fixed columns (page_name -> ual_page_name, action -> ual_action).
 * So we encode the richer structure INSIDE those existing fields:
 *   - `page_name`  carries a stable Operation category (see AUDIT_OPERATION)
 *   - `action`     carries "[OUTCOME] <message> / key: value / key: value"
 *
 * The brij-pwa Audit report parses the "[OUTCOME]" tag into an OK/FAIL badge
 * and splits the remaining " / " segments into readable extra-details.
 * Legacy free-text `action` strings (no tag) still render fine (treated as INFO).
 */

export const AUDIT_OUTCOME = {
  SUCCESS: "SUCCESS",
  FAILED: "FAILED",
  INFO: "INFO"
};

// Stable operation categories -> stored in page_name (the report's "Operation" column).
export const AUDIT_OPERATION = {
  LOGIN: "Login",
  LOGIN_FAILED: "Login Failed",
  LOGOUT: "Logout",
  KITCHEN_LOGIN: "Kitchen Login",
  SHIFT_OPEN: "Shift Open",
  SHIFT_CLOSE: "Shift Close",
  CASH_DRAWER: "Cash Drawer",
  FLOAT_CASH: "Float Cash",
  PAY_IN: "Cash Pay-In",
  PAY_OUT: "Cash Pay-Out",
  NO_SALE: "No Sale",
  REFUND: "Refund",
  VOID: "Void / Cancel",
  DISCOUNT_OVERRIDE: "Discount Override",
  PRICE_OVERRIDE: "Price Override",
  MANAGER_AUTH: "Manager Authorization",
  OFFER: "Offer",
  VOUCHER: "Voucher",
  WALLET: "Wallet",
  PAYMENT: "Payment",
  BILL: "Generate Bill",
  BOOKING: "Booking",
  FNB: "F&B",
  CART: "Cart",
  SEATS: "Seats",
  TICKET_PRINT: "Ticket Print",
  SMS: "SMS",
  NAVIGATION: "Navigation"
};

// Delimiter the brij-pwa report already splits on for extra-details.
export const AUDIT_DELIM = " / ";

function stringifyDetail(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  }
  return String(value);
}

/**
 * Build the ual_action string: "[OUTCOME] <message> / key: value / key: value".
 * @param {string} outcome one of AUDIT_OUTCOME
 * @param {string} message short human-readable summary
 * @param {object} [details] flat map of extra key/value pairs
 */
export function formatAuditAction(outcome, message, details) {
  const tag = AUDIT_OUTCOME[outcome] ? `[${outcome}]` : `[${AUDIT_OUTCOME.INFO}]`;
  const parts = [];
  parts.push(message ? `${tag} ${message}` : tag);
  if (details && typeof details === "object") {
    Object.keys(details).forEach(key => {
      const val = stringifyDetail(details[key]);
      if (val !== "") parts.push(`${key}: ${val}`);
    });
  }
  return parts.join(AUDIT_DELIM);
}



// WEBPACK FOOTER //
// ./src/constants/auditLog.js